import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Stack, Button, TextField, MenuItem, Typography, Snackbar, Alert } from '@mui/material';
import { PageHeader, DetailCard } from '../components';
import { useCreateCount } from '../hooks/useInventoryCounts';
import { useWarehouses } from '../hooks/useWarehouses';
import { useBusinessUuid } from '../useBusinessUuid';
import { colors, COUNT_TYPES, humanize } from '../constants';

export default function InventoryCountCreate() {
  const history = useHistory();
  const businessUuid = useBusinessUuid();
  const create = useCreateCount();
  const { data: warehouses = [] } = useWarehouses();

  const [warehouseUuid, setWarehouseUuid] = useState('');
  const [type, setType] = useState('full');
  const [scheduledDate, setScheduledDate] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    setError(null);
    try {
      const created = await create.mutateAsync({
        businessUuid,
        warehouseUuid: warehouseUuid || undefined,
        type,
        scheduledDate: scheduledDate.trim() || new Date().toISOString().slice(0, 10),
        notes: notes || undefined,
      });
      const uuid = created?.uuid;
      if (uuid) history.replace(`/inventory-management/inventory-counts/${uuid}`);
      else history.replace('/inventory-management/inventory-counts');
    } catch (e) {
      setError(e?.response?.data?.message || 'Something went wrong');
    }
  };

  return (
    <>
      <PageHeader title="New Count" backTo="/inventory-management/inventory-counts" />
      <Box sx={{ maxWidth: 640 }}>
        <DetailCard title="Count Details">
          <Stack spacing={2}>
            <TextField
              select
              label="Warehouse"
              value={warehouseUuid}
              onChange={(e) => setWarehouseUuid(e.target.value)}
              fullWidth
              size="small"
              helperText="Leave empty to count all products"
            >
              <MenuItem value="">All products</MenuItem>
              {warehouses.map((w) => (
                <MenuItem key={w.uuid} value={w.uuid}>{w.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Count Type"
              value={type}
              onChange={(e) => setType(e.target.value)}
              fullWidth
              size="small"
              required
            >
              {COUNT_TYPES.map((t) => (
                <MenuItem key={t} value={t}>{humanize(t)}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Scheduled Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={3}
            />
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={create.isPending}
              sx={{ backgroundColor: colors.primary, textTransform: 'none' }}
            >
              {create.isPending ? 'Creating...' : 'Create Count'}
            </Button>
          </Stack>
        </DetailCard>
      </Box>
      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </>
  );
}
